import { useEffect, useState } from "react";
import { useSearch } from "wouter";
import { MapPin } from "lucide-react";
import { accountRegionSchema } from "@shared/validation/accountRegion.schema";
import { useAccountRegion, useUpdateAccountRegion } from "../lib/api/useAccountRegion";
import { useToast } from "../lib/toast/ToastProvider";
import { ApiError } from "../lib/api/client";
import { AppShell } from "../components/AppShell";
import { PlayfulHero } from "../components/PlayfulHero";
import { SectionCard } from "../components/ui/SectionCard";
import { Button } from "../components/ui/Button";
import { Spinner } from "../components/ui/Spinner";
import { EmptyState } from "../components/ui/EmptyState";
import { RegionPicker, type RegionPickerValue } from "../components/account/RegionPicker";

/**
 * Settings → Region: "Where is your family based?". Picks the version of the
 * product (users.market) and, in the Philippines, the city the season cards
 * are written for. The same accountRegionSchema validates here and on the
 * server, so the errors match.
 */
export function RegionSettings() {
  const { data, isLoading, isError, error, refetch, isRefetching } = useAccountRegion();
  const update = useUpdateAccountRegion();
  const { publish } = useToast();
  const [draft, setDraft] = useState<RegionPickerValue | null>(null);
  const [cityError, setCityError] = useState<string | undefined>();
  // Home's "Are you in the Philippines?" card links here with ?market=ph. It
  // only pre-picks the Philippines in this form; nothing is saved until the
  // parent chooses a city and presses Save (the server validates as always).
  const suggestPh = new URLSearchParams(useSearch()).get("market") === "ph";

  // Initialise the draft once, so a background refetch never clobbers a choice in progress.
  useEffect(() => {
    if (data && draft === null) {
      setDraft({ market: suggestPh && data.market === "main" ? "ph" : data.market, phRegion: data.phRegion });
    }
  }, [data, draft, suggestPh]);

  const dirty =
    draft !== null &&
    data !== undefined &&
    (draft.market !== data.market || (draft.market === "ph" && draft.phRegion !== data.phRegion));

  const onSave = async () => {
    if (!draft) return;
    const parsed = accountRegionSchema.safeParse(draft);
    if (!parsed.success) {
      setCityError(parsed.error.issues.find((i) => i.path[0] === "phRegion")?.message ?? "Choose your city");
      return;
    }
    setCityError(undefined);
    try {
      const saved = await update.mutateAsync(parsed.data);
      setDraft({ market: saved.market, phRegion: saved.phRegion ?? draft.phRegion });
      publish({
        variant: "success",
        title: "Region saved",
        description:
          saved.market === "ph"
            ? `Season cards for ${saved.phRegion} will show on your dashboard.`
            : "You're on the main version of Leena's List.",
      });
    } catch (err) {
      publish({
        variant: "error",
        title: "Couldn't save your region",
        description: err instanceof ApiError ? err.message : "Please try again.",
      });
    }
  };

  return (
    <AppShell maxWidth="3xl">
      <PlayfulHero
        accent="amber"
        decor="compact"
        back={{ href: "/dashboard", label: "Back to dashboard" }}
        eyebrow="Settings"
        title="Region"
        description="Tell us where your family is based, so Leena's List shows what's useful where you live."
      />

      <div className="mt-8">
        {isLoading || (draft === null && !isError) ? (
          <SectionCard as="div" icon={MapPin} title="Your region" accent="amber">
            <div role="status" className="flex items-center gap-2.5 text-sm text-neutral-600">
              <Spinner size="sm" /> Loading your region…
            </div>
          </SectionCard>
        ) : isError || draft === null ? (
          <EmptyState
            icon={MapPin}
            title="Couldn't load your region"
            description={error instanceof ApiError ? error.message : "Something went wrong. Please try again."}
            action={
              <Button variant="secondary" isLoading={isRefetching} onClick={() => refetch()}>
                Try again
              </Button>
            }
          />
        ) : (
          <SectionCard as="div" icon={MapPin} title="Your region" accent="amber">
            <RegionPicker
              value={draft}
              onChange={(next) => {
                setDraft(next);
                if (next.phRegion) setCityError(undefined);
              }}
              cityError={cityError}
              disabled={update.isPending}
            />
            <div className="mt-6 flex items-center justify-end gap-3">
              <span
                aria-live="polite"
                className={dirty ? "text-xs text-neutral-600" : "text-xs text-neutral-600 opacity-0"}
              >
                You have unsaved changes
              </span>
              <Button onClick={onSave} disabled={!dirty} isLoading={update.isPending}>
                Save region
              </Button>
            </div>
          </SectionCard>
        )}
      </div>
    </AppShell>
  );
}
